import React, { useEffect, useState } from "react";
import {
	UseControllerProps,
	useController,
	useFormContext
} from "react-hook-form";
import { TextActionOptional } from "src/features/observations/components/TextActionOptional";
import { UserSearch } from "src/containers";
import { UserSearchItem } from "src/components";
import { X } from "lucide-react";
import useSingleStoreUser from "src/hooks/useSingleStoreUser/useSingleStoreUser";

interface EmployeeSelectionQuestionProps extends UseControllerProps<ObservationFormState> {
	questionNumber: number;
	staticInput?: boolean;
	showAction?: boolean;
}

const EmployeeSelectionQuestion: React.FC<EmployeeSelectionQuestionProps> = ({
	questionNumber,
	name,
	rules,
	showAction,
	staticInput
}) => {
	const { control } = useFormContext<ObservationFormState>();
	const {
		field: { onChange, value }
	} = useController({ control, name, rules });

	const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

	const { data: user } = useSingleStoreUser(selectedUserId, {
		enabled: selectedUserId !== null
	});

	const [selectedSearchItem, setSelectedSearchItem] =
		useState<Partial<StoreUser> | null>(user ?? null);

	useEffect(() => {
		const valueSplit: string[] | undefined = value?.split(" | ");

		if (valueSplit && valueSplit.length === 3) {
			setSelectedUserId(Number(valueSplit[0]));
		}
	}, [value]);

	useEffect(() => {
		if (selectedSearchItem !== user) setSelectedSearchItem(user ?? null);
	}, [user]);

	const onUserSearchChange = (user: Partial<StoreUser>) => {
		if (staticInput) return;

		setSelectedSearchItem(user);
		if (user && user.id) {
			onChange(`${user.id} | ${user.name} | ${user.employee_id ?? ""}`);
		}
	};

	return (
		<div>
			<div className="w-full">
				{selectedSearchItem && selectedSearchItem.id ? (
					<UserSearchItem
						backgroundColor="white"
						borderRadius="5px"
						paddingX={6}
						paddingY={4}
						user={selectedSearchItem as StoreUser}
						iconEvent={() => {
							if (!staticInput) {
								setSelectedSearchItem(null);
								setSelectedUserId(null);
								onChange(null);
							}
						}}
						icon={staticInput ? undefined : <X size={24} />}
					/>
				) : (
					<UserSearch
						value={selectedSearchItem?.name ?? ""}
						onChange={onUserSearchChange}
					/>
				)}
			</div>
			{showAction && (
				<TextActionOptional
					name={`questions.${questionNumber - 1}.action`}
					questionNumber={questionNumber}
					staticInput={staticInput}
				/>
			)}
		</div>
	);
};

export { EmployeeSelectionQuestion, type EmployeeSelectionQuestionProps };
